import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangleIcon, BanIcon, RefreshCwIcon, RotateCcwIcon, ShieldCheckIcon, Trash2Icon, UnlockIcon } from 'lucide-react';
import { AdminUserRow, blockUser, deleteOrganization, fetchUsersOverview, resetOrganization, unblockUser } from '../../services/adminService';
import { ensureDefaultContent } from '../../services/repair';
import { refresh as refreshStore } from '../../services/store';
import { formatDate } from '../../utils/format';
import { ConfirmDialog } from '../crm/ConfirmDialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Tag } from '../ui/Tag';
import { toast } from '../ui/Toast';

/** Resumen de la última sincronización masiva de plantillas. */
interface SyncSummary {
  total: number;
  updated: number;
  failed: number;
}

/**
 * Panel reservado al super administrador de la plataforma. Solo se monta
 * cuando `useAuth().isSuperAdmin` es true; además, cada RPC vuelve a validar
 * el permiso en la base de datos.
 */
export function AdminSection() {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Diálogos de confirmación.
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);

  // Actualización masiva de plantillas.
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    done: 0,
    total: 0
  });
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchUsersOverview());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  /** Vacía la organización y vuelve a sembrar formulario y landings. */
  async function handleReset(row: AdminUserRow) {
    setBusyId(row.organization_id);
    try {
      await resetOrganization(row.organization_id);
      await ensureDefaultContent(row.organization_id, row.organization_name ?? 'Mi empresa');
      await refreshStore();
      toast.success('Organización reiniciada', {
        description: `${row.organization_name ?? 'La organización'} vuelve a tener su formulario y sus landings por defecto.`
      });
      await load();
    } catch (err) {
      toast.error('No se ha podido reiniciar', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
      setResetTarget(null);
    }
  }

  /** Bloquea o reactiva el acceso del usuario. */
  async function handleToggleBlock(row: AdminUserRow) {
    setBusyId(row.user_id);
    try {
      if (row.is_blocked) {
        await unblockUser(row.user_id);
        toast.success('Acceso reactivado');
      } else {
        await blockUser(row.user_id);
        toast.success('Acceso bloqueado');
      }
      await load();
    } catch (err) {
      toast.error('No se ha podido cambiar el acceso', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
    }
  }

  /** Elimina la organización completa; el usuario solo si no tiene otra. */
  async function handleDelete(row: AdminUserRow) {
    setBusyId(row.organization_id);
    try {
      const result = await deleteOrganization(row.organization_id);
      toast.success('Organización eliminada', {
        description: `${result.deleted_users ?? 0} usuario(s) eliminados · ${result.kept_users ?? 0} conservados por pertenecer a otra organización.`
      });
      await load();
    } catch (err) {
      toast.error('No se ha podido eliminar', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  }

  /**
   * Recorre todas las organizaciones y completa su contenido con la plantilla
   * oficial. `ensureDefaultContent` es idempotente: nunca sobrescribe nada.
   */
  async function handleSyncAll() {
    setSyncOpen(false);
    setSyncing(true);
    setSyncSummary(null);
    let updated = 0;
    let failed = 0;
    const targets = rows.filter((row, index) => rows.findIndex(r => r.organization_id === row.organization_id) === index);
    setSyncProgress({
      done: 0,
      total: targets.length
    });
    for (const [index, row] of targets.entries()) {
      try {
        const result = await ensureDefaultContent(row.organization_id, row.organization_name ?? 'Mi empresa');
        if (result.created || result.addedQuestions > 0) updated += 1;
      } catch (err) {
        failed += 1;
        console.error(`[AVA] Actualización fallida en ${row.organization_id}:`, err);
      }
      setSyncProgress({
        done: index + 1,
        total: targets.length
      });
    }
    await refreshStore();
    setSyncing(false);
    setSyncSummary({
      total: targets.length,
      updated,
      failed
    });
    if (failed === 0) toast.success('Sistema actualizado', {
      description: `${targets.length} organizaciones revisadas · ${updated} actualizadas.`
    });else toast.error('Actualización con incidencias', {
      description: `${failed} organizaciones no se han podido actualizar.`
    });
  }
  const columns = [{
    key: 'name',
    header: 'Usuario',
    render: (row: AdminUserRow) => <div className="min-w-0" data-id="element-669" data-mp="3f685xnd11ius">
          <p className="truncate font-medium text-foreground" data-id="element-670" data-mp="3p0m3yhww426i">
            {row.full_name || 'Sin nombre'}
          </p>
          <p className="truncate text-xs text-muted-foreground" data-id="element-671" data-mp="3kzk3xyv5fm3h">
            {row.email || '—'}
          </p>
        </div>
  }, {
    key: 'organization',
    header: 'Organización',
    render: (row: AdminUserRow) => <span className="text-sm text-foreground" data-id="element-672" data-mp="3azjfjyifafsf">
          {row.organization_name || '—'}
        </span>
  }, {
    key: 'created',
    header: 'Alta',
    render: (row: AdminUserRow) => <span className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-673" data-mp="31a9pspb1x97h">
          {formatDate(row.created_at)}
        </span>
  }, {
    key: 'counts',
    header: 'Contenido',
    render: (row: AdminUserRow) => <span className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-674" data-mp="2sawahg08xqeu">
          {row.leads_count} leads · {row.forms_count} form. ·{' '}
          {row.landings_count} landings
        </span>
  }, {
    key: 'status',
    header: 'Estado',
    render: (row: AdminUserRow) => row.is_blocked ? <Tag className="bg-red-50 text-red-700" data-id="element-675" data-mp="288i8q5sdqoll">Bloqueado</Tag> : <Tag className="bg-green-50 text-green-700" data-id="element-676" data-mp="28a32vuas85ix">Activo</Tag>
  }, {
    key: 'actions',
    header: '',
    className: 'text-right',
    render: (row: AdminUserRow) => <div className="flex flex-wrap justify-end gap-2" data-id="element-677" data-mp="2i77q7xgd671b">
          <Button size="sm" variant="secondary" leftIcon={<RotateCcwIcon className="h-3.5 w-3.5" data-id="element-679" data-mp="2n4hgtgfhooqq" />} disabled={busyId === row.organization_id} onClick={() => setResetTarget(row)} data-id="element-678" data-mp="2n4pw1ez34653">
            Reiniciar
          </Button>
          <Button size="sm" variant="secondary" leftIcon={row.is_blocked ? <UnlockIcon className="h-3.5 w-3.5" data-id="element-681" data-mp="1zff0qd0i73yc" /> : <BanIcon className="h-3.5 w-3.5" data-id="element-682" data-mp="1zedm5yka3zbv" />} disabled={busyId === row.user_id} onClick={() => handleToggleBlock(row)} data-id="element-680" data-mp="1uhdh9qz612mc">
            {row.is_blocked ? 'Reactivar' : 'Bloquear'}
          </Button>
          <Button size="sm" variant="destructive" leftIcon={<Trash2Icon className="h-3.5 w-3.5" data-id="element-684" data-mp="16owxnbzs52kx" />} disabled={busyId === row.organization_id} onClick={() => {
        setDeleteConfirmText('');
        setDeleteTarget(row);
      }} data-id="element-683" data-mp="16pol1zj83u04">
            Eliminar
          </Button>
        </div>
  }];
  return <div className="space-y-4" data-id="element-685" data-mp="2lvh3h88nzntv">
      <div className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4" data-id="element-686" data-mp="2lwij7q0mkyhx">
        <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" data-id="element-687" data-mp="2zw1n26melnof" />
        <p className="text-sm text-muted-foreground" data-id="element-688" data-mp="2uucbmpv9r7zt">
          Panel reservado al super administrador de la plataforma. Las acciones
          afectan a organizaciones de clientes reales y no se pueden deshacer.
        </p>
      </div>

      <Tabs defaultValue="usuarios" data-id="element-689" data-mp="34rh2v43z1tnh">
        <TabsList data-id="element-690" data-mp="3ilhsn8cy46bp">
          <TabsTrigger value="usuarios" data-id="element-691" data-mp="3ijwvrc35sbao">Usuarios</TabsTrigger>
          <TabsTrigger value="actualizaciones" data-id="element-692" data-mp="3dlvcaq1tm9yo">Actualizaciones</TabsTrigger>
        </TabsList>

        <TabsContent value="usuarios" data-id="element-693" data-mp="3sj5o60idlhux">
          <div className="space-y-3 rounded-lg border border-border bg-background p-5" data-id="element-694" data-mp="3sk7c35khpyu2">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-695" data-mp="3nkudxoo655c6">
              <p className="text-sm text-muted-foreground" data-id="element-696" data-mp="b0qcmnrjhlek">
                {loading ? 'Cargando organizaciones…' : `${rows.length} usuarios en la plataforma`}
              </p>
              <Button size="sm" variant="ghost" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-698" data-mp="132j215o6jxz" />} onClick={load} loading={loading} data-id="element-697" data-mp="ezpnsq3v6eeg">
                Actualizar lista
              </Button>
            </div>

            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-699" data-mp="3ni3z5a02t5jr">
                {error}
              </p>}

            <div className="overflow-x-auto" data-id="element-700" data-mp="3s8u2khoxyybt">
              <Table columns={columns} data={rows} rowKey={row => `${row.user_id}-${row.organization_id}`} emptyMessage={loading ? 'Cargando…' : 'No hay organizaciones registradas.'} data-id="element-701" data-mp="3saop20fv2yi0" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="actualizaciones" data-id="element-702" data-mp="34iho0nz6y3c9">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-703" data-mp="34jrofjtinnm2">
            <div data-id="element-704" data-mp="39hkt28f689k8">
              <h3 className="text-sm font-semibold text-foreground" data-id="element-705" data-mp="39k6zu5u8ml1e">
                Actualizar sistema
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground" data-id="element-706" data-mp="2evl1nrxz4a3u">
                Revisa todas las organizaciones y completa el formulario
                predeterminado y las landings con lo que falte de la plantilla
                oficial. No borra ni sobrescribe contenido personalizado.
              </p>
            </div>

            {syncing && <div className="space-y-2" data-id="element-707" data-mp="2soalbpq152xs">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface" data-id="element-708" data-mp="2spc1u9ptehmw">
                  <div className="h-full rounded-full bg-primary transition-all" style={{
                width: `${syncProgress.total ? syncProgress.done / syncProgress.total * 100 : 0}%`
              }} data-id="element-709" data-mp="2xo5ha7onwipp" />
                </div>
                <p className="text-sm text-muted-foreground" data-id="element-710" data-mp="3bm3nuqwqraun">
                  Actualizando {syncProgress.done} de {syncProgress.total}…
                </p>
              </div>}

            {syncSummary && !syncing && <div className="rounded-lg bg-surface p-4 text-sm text-muted-foreground" data-id="element-711" data-mp="14x1p229j4jfd">
                <p className="font-medium text-foreground" data-id="element-712" data-mp="zyrt3pfzdesp">
                  Resumen de la actualización
                </p>
                <p className="mt-1" data-id="element-713" data-mp="1emcw7jcuve0m">
                  {syncSummary.total} organizaciones revisadas ·{' '}
                  {syncSummary.updated} actualizadas · {syncSummary.failed}{' '}
                  con errores
                </p>
              </div>}

            <Button leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-715" data-mp="gzw1mlvibeik" />} onClick={() => setSyncOpen(true)} loading={syncing} disabled={rows.length === 0} data-id="element-714" data-mp="m0k01sm2exmt">
              Actualizar sistema
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reiniciar organización */}
      <ConfirmDialog open={Boolean(resetTarget)} title="Reiniciar organización" description={resetTarget ? `Se borrarán los leads, notas, formularios y landings de ${resetTarget.organization_name ?? 'esta organización'} y se volverán a crear el formulario y las landings por defecto. El usuario y sus credenciales no se tocan.` : ''} confirmLabel="Reiniciar" loading={busyId === resetTarget?.organization_id} onCancel={() => setResetTarget(null)} onConfirm={() => resetTarget && handleReset(resetTarget)} data-id="element-716" data-mp="r06fm8pd8pys" />

      {/* Actualización masiva */}
      <ConfirmDialog open={syncOpen} title="Actualizar todas las organizaciones" description={`Se revisarán ${rows.length} organizaciones y se completará su contenido con la plantilla oficial. Es una operación segura: no borra ni sobrescribe datos personalizados.`} confirmLabel="Actualizar" onCancel={() => setSyncOpen(false)} onConfirm={handleSyncAll} data-id="element-717" data-mp="1d248ws5snfrh" />

      {/* Eliminación definitiva con confirmación escrita */}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Eliminar organización y usuario" footer={<div className="flex justify-end gap-2" data-id="element-719" data-mp="3tq81wubzjcxu">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-id="element-720" data-mp="1lw0ia3ola7w">
              Cancelar
            </Button>
            <Button variant="destructive" disabled={deleteConfirmText.trim() !== 'ELIMINAR'} loading={busyId === deleteTarget?.organization_id} onClick={() => deleteTarget && handleDelete(deleteTarget)} data-id="element-721" data-mp="3q797h3991ezs">
              Eliminar definitivamente
            </Button>
          </div>} data-id="element-718" data-mp="u3rvgu7unjq3">
        {deleteTarget && <div className="space-y-4" data-id="element-722" data-mp="3c7q2gjbqijrp">
            <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4" data-id="element-723" data-mp="3cavp7rww59k9">
              <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" data-id="element-724" data-mp="37b9b7g98npae" />
              <p className="text-sm text-red-700" data-id="element-725" data-mp="32i6gv5wn0bt5">
                Esta acción elimina la organización en cascada y no se puede
                deshacer. El usuario solo se borrará si no pertenece a otra
                organización.
              </p>
            </div>

            <dl className="space-y-2 rounded-lg bg-surface p-4 text-sm" data-id="element-726" data-mp="2ojqaxh6lmpc2">
              <div className="flex justify-between gap-4" data-id="element-727" data-mp="2ol1h1nnio96c">
                <dt className="text-muted-foreground" data-id="element-728" data-mp="2jlf3fd3s0qx0">Organización</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-729" data-mp="ngpf416vqfly">
                  {deleteTarget.organization_name ?? '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-730" data-mp="dr5wmwn2wnrm">
                <dt className="text-muted-foreground" data-id="element-731" data-mp="dqmou8r57frb">Email</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-732" data-mp="ippmh84smjp9">
                  {deleteTarget.email ?? '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-733" data-mp="4skiddon3uwn">
                <dt className="text-muted-foreground" data-id="element-734" data-mp="8t4gokky94kd">Se perderán</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-735" data-mp="3qxxxqn9fljhq">
                  {deleteTarget.leads_count} leads ·{' '}
                  {deleteTarget.forms_count} formularios ·{' '}
                  {deleteTarget.landings_count} landings
                </dd>
              </div>
            </dl>

            <Input id="admin-delete-confirm" label="Escribe ELIMINAR para confirmar" placeholder="ELIMINAR" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} data-id="element-736" data-mp="1u5xmp2wr5ixr" />
          </div>}
      </Modal>
    </div>;
}